import React, { useEffect, useRef, useState } from 'react';
import { useScrollYPosition } from 'react-use-scroll-position';
import styled from 'styled-components';
import get from 'lodash-es/get';
import sum from 'lodash-es/sum';
import findIndex from 'lodash-es/findIndex';
import { motion } from 'framer-motion';
import last from 'lodash-es/last';

const Stack = styled(motion.div)`
  display: block;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  background: white;
  z-index: 0;
  opacity: 0;
`;

const Page = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  background: white;
  z-index: ${props => props.zIndex};
  overflow-x: hidden;
`;

const atBottom = ele => ele.getBoundingClientRect().bottom <= 0;
const atTop = ele => ele.getBoundingClientRect().top >= 0;

const replaceHash = el => {
  const id = get(el, 'firstChild.id');
  if (id) {
    if (window.history.pushState) {
      window.history.pushState(null, null, `#${id}`);
    } else {
      window.location.hash = `#${id}`;
    }
  }
};

const changeActivePageDown = (curr, next) => {
  if (next) {
    next.classList.add('active');
    replaceHash(next);
  }
};

const changeActivePageUp = (curr, prev, next) => {
  curr.classList.remove('active');
  if (prev) {
    prev.classList.add('active');
    replaceHash(prev);
  }

  if (next) {
    next.classList.remove('active');
  }
};

const isActive = (child, hash) => get(child, 'props.id') === hash;

const VerticalStack = ({ children, location }) => {
  const ref = useRef();
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const hash = get(location, 'hash', '').replace('#', '');
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo(0, 0);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  useEffect(() => {
    try {
      setHeight(sum([...ref.current.children].map(_ => _.clientHeight)));
    } catch (e) {
      console.warn(e);
    }
  }, [setHeight]);

  const offsetY = useScrollYPosition();

  useEffect(() => {
    if (ref && !ref.current && offsetY) {
      return;
    }
    const root = ref.current;
    const curr = last(root.querySelectorAll('.active')) || root.firstChild;

    const next = get(curr, 'nextSibling');
    const prev = get(curr, 'previousSibling');

    // down scroll
    if (next && atBottom(curr)) {
      changeActivePageDown(curr, next);
    } else if (curr && prev && atTop(curr)) {
      // up scroll
      changeActivePageUp(curr, prev, next);
    }
  }, [offsetY]);

  const hash = get(location, 'hash', '').replace('#', '');
  const activeChild = Math.max(
    findIndex(children, child => isActive(child, hash)),
    0
  );

  return (
    <Stack
      ref={ref}
      style={{ minHeight: height, opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ opacity: { duration: 0.3 } }}
    >
      {children.map((child, id) => (
        <Page
          key={id}
          className={id <= activeChild ? 'active' : undefined}
          zIndex={100 - id}
        >
          {child}
        </Page>
      ))}
    </Stack>
  );
};

export default VerticalStack;
